import React, { useMemo, useState } from 'react';
import { useAuthUser } from 'react-auth-kit';

import Input from '../Input';
import Checkbox from '../Checkbox';
import ColorPicker from '../ColorPicker';

import { useAccessToken } from 'src/hooks/useAccessToken';
import { config } from 'src/utils/config';
import { getThumbnailUrl } from 'src/utils/previews';

import {
  ShareSettings,
  WatermarkPlacement,
  VIDEO_OPTIONS,
  VIDEO_PREVIEW_QUALITY_OPTIONS,
  LOGO_SOURCE_OPTIONS,
  WATERMARK_PLACEMENT_OPTIONS,
  isValidAssetId,
} from './shareSettings';

import styles from './ShareSettingsForm.module.scss';

export type ShareSettingsFormProps = {
  value: ShareSettings;
  onChange: (next: ShareSettings) => void;
  /** Show the print-ready (CMYK) download toggle (addon-gated by the caller). */
  showPrintReady?: boolean;
};

export const ShareSettingsForm: React.FC<ShareSettingsFormProps> = ({
  value,
  onChange,
  showPrintReady = false,
}) => {
  const auth = useAuthUser();
  const accessToken = useAccessToken();
  const [logoThumbnailError, setLogoThumbnailError] = useState(false);

  const set = <K extends keyof ShareSettings>(key: K, v: ShareSettings[K]) => {
    onChange({ ...value, [key]: v });
  };

  const toggleVideoOption = (option: string) => {
    const next = value.selectedVideoOptions.includes(option)
      ? value.selectedVideoOptions.filter((v) => v !== option)
      : [...value.selectedVideoOptions, option];
    set('selectedVideoOptions', next);
  };

  const logoThumbnailUrl = useMemo(() => {
    setLogoThumbnailError(false);
    if (value.logoSource === 'watermark') {
      return `${config.hostUrl}/api/v1/user/watermark/${auth()?.userId}?_cb=${Date.now()}`;
    }
    if (value.logoSource === 'asset' && isValidAssetId(value.logoAssetId)) {
      return getThumbnailUrl({
        id: value.logoAssetId,
        width: 100,
        height: 100,
        jwt: accessToken,
      });
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.logoSource, value.logoAssetId, accessToken]);

  return (
    <>
      {/* Preview & Feedback Options */}
      <div className={styles.previewFeedbackSection}>
        <label className={styles.sectionTitle}>Preview & Feedback Options</label>

        <div className={styles.previewSettingsGroup}>
          <label className={styles.subSectionLabel}>Preview Settings</label>
          <div className={styles.previewImageRadioGroup}>
            <label className={styles.radioItem}>
              <Checkbox
                radio
                checked={value.previewImageMode === 'odi'}
                onChange={() => set('previewImageMode', 'odi')}
                name="previewImageMode"
              />
              <span>Normal</span>
            </label>
            <label className={styles.radioItem}>
              <Checkbox
                radio
                checked={value.previewImageMode === 'odi_wm'}
                onChange={() => set('previewImageMode', 'odi_wm')}
                name="previewImageMode"
              />
              <span>Branded</span>
            </label>
          </div>
          <div className={styles.videoPreviewGroup}>
            <label className={styles.formatLabel}>Video Preview Quality</label>
            <div className={styles.previewImageRadioGroup}>
              {VIDEO_PREVIEW_QUALITY_OPTIONS.map((option) => (
                <label key={option.value} className={styles.radioItem}>
                  <Checkbox
                    radio
                    checked={value.previewVideoQuality === option.value}
                    onChange={() => set('previewVideoQuality', option.value)}
                    name="previewVideoQuality"
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.feedbackSettingsGroup}>
          <label className={styles.subSectionLabel}>Feedback Settings</label>
          <div className={styles.compactToggleRow}>
            <label className={styles.compactToggleItem}>
              <Checkbox
                checked={value.allowFeedback}
                onChange={(e) => set('allowFeedback', e.target.checked)}
              />
              <span>Allow Feedback</span>
            </label>
            <label className={styles.compactToggleItem}>
              <Checkbox
                checked={value.allowApproval}
                onChange={(e) => set('allowApproval', e.target.checked)}
              />
              <span>Allow Approval</span>
            </label>
          </div>
        </div>
      </div>

      {/* Branding */}
      <div className={styles.downloadOptionsSection}>
        <label className={styles.sectionTitle}>Branding</label>
        <p className={styles.brandingDescription}>
          These branding options apply to image downloads and image previews. For
          branded video, transcodes are generated using account watermark
          automatically.
        </p>

        <div className={styles.logoSourceSection}>
          <label className={styles.subLabel}>Brand Image</label>
          <div className={styles.logoInputRow}>
            <div className={styles.logoInputGroup}>
              <select
                className={styles.logoSelect}
                value={value.logoSource}
                onChange={(e) =>
                  set('logoSource', e.target.value as 'watermark' | 'asset' | '')
                }
              >
                {LOGO_SOURCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {value.logoSource === 'asset' && (
                <Input
                  placeholder="Enter 32-character asset ID for logo"
                  value={value.logoAssetId}
                  onChange={(v) => set('logoAssetId', v)}
                  className={styles.logoAssetInput}
                />
              )}
            </div>
            {logoThumbnailUrl && !logoThumbnailError && (
              <div className={styles.logoThumbnailPreview}>
                <img
                  src={logoThumbnailUrl}
                  alt="Logo preview"
                  onError={() => setLogoThumbnailError(true)}
                />
              </div>
            )}
            {logoThumbnailError && (
              <div className={styles.logoThumbnailError}>Unable to load preview</div>
            )}
          </div>
          {value.logoSource === '' && (
            <p className={styles.toggleDescription}>
              Your account watermark will be shown as logo. You can configure this
              in Account Settings.
            </p>
          )}
        </div>

        <div className={styles.colorPickersRow}>
          <div className={styles.colorPickerGroup}>
            <span className={styles.formLabel}>Primary Color</span>
            <ColorPicker
              value={value.primaryColor}
              onChange={(c) => set('primaryColor', c)}
            />
          </div>
          <div className={styles.colorPickerGroup}>
            <span className={styles.formLabel}>Background Color</span>
            <ColorPicker
              value={value.backgroundColor}
              onChange={(c) => set('backgroundColor', c)}
            />
          </div>
        </div>

        <div className={styles.watermarkSettings}>
          <label className={styles.watermarkFieldLabel}>Brand Image Options</label>
          <div className={styles.watermarkSettingsRow}>
            <div className={styles.watermarkField}>
              <label className={styles.watermarkFieldLabel}>
                Brand Image Placement
              </label>
              <select
                className={styles.placementSelect}
                value={value.wmPlacement}
                onChange={(e) =>
                  set('wmPlacement', e.target.value as WatermarkPlacement)
                }
              >
                {WATERMARK_PLACEMENT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.watermarkField}>
              <label className={styles.watermarkFieldLabel}>
                Brand Image Scaling: {value.wmScale}%
              </label>
              <input
                type="range"
                min="1"
                max="100"
                value={value.wmScale}
                onChange={(e) => set('wmScale', Number(e.target.value))}
                className={styles.scaleSlider}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Download Options */}
      <div className={styles.downloadOptionsSection}>
        <label className={styles.sectionTitle}>Download Options</label>

        <div className={styles.toggleRow}>
          <div>
            <span className={styles.toggleLabel}>Allow original file download</span>
            <p className={styles.toggleDescription}>
              Let recipients download the original uploaded files
            </p>
          </div>
          <Checkbox
            checked={value.includeOriginal}
            onChange={(e) => set('includeOriginal', e.target.checked)}
          />
        </div>

        <div className={styles.optionGroup}>
          <label className={styles.optionGroupTitle}>Image Downloads</label>
          <div className={styles.videoGrid}>
            <label className={styles.checkboxItem}>
              <Checkbox
                checked={value.enableCustomSize}
                onChange={(e) => set('enableCustomSize', e.target.checked)}
              />
              <span>Social/Custom Size Image Downloads</span>
            </label>
            <label className={styles.checkboxItem}>
              <Checkbox
                checked={value.enableWatermarkedCustomSize}
                onChange={(e) =>
                  set('enableWatermarkedCustomSize', e.target.checked)
                }
              />
              <span>Branded Image Downloads</span>
            </label>
          </div>
        </div>

        {showPrintReady && (
          <div className={styles.optionGroup}>
            <label className={styles.optionGroupTitle}>Print-ready Downloads</label>
            <p className={styles.optionGroupDescription}>
              Serve the print-ready (CMYK, 300 DPI) file for assets that have a
              print export. Only assets that already have a print-ready conversion
              are downloadable.
            </p>
            <label className={styles.checkboxItem}>
              <Checkbox
                checked={value.printReadyDownload}
                onChange={(e) => set('printReadyDownload', e.target.checked)}
              />
              <span>Allow print-ready (CMYK) download</span>
            </label>
          </div>
        )}

        <div className={styles.optionGroup}>
          <label className={styles.optionGroupTitle}>Video Downloads</label>
          <div className={styles.videoGrid}>
            {VIDEO_OPTIONS.map((option) => (
              <label key={option.value} className={styles.checkboxItem}>
                <Checkbox
                  checked={value.selectedVideoOptions.includes(option.value)}
                  onChange={() => toggleVideoOption(option.value)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default ShareSettingsForm;
